import { useCallback, useEffect, useRef, useState } from 'react';
import type { TableWithSessionDTO } from '@scan2eat/shared';
import { formatMoney } from '@scan2eat/shared';
import { apiFetch, apiFetchBlob, getAccessToken } from '../api/client.js';
import { useAuth } from '../auth/context.js';
import { useRealtimeSync } from '../realtime/useRealtimeSync.js';
import { ConfirmDialog } from '../components/ConfirmDialog.js';
import { DropdownMenu, DropdownItem } from '../components/DropdownMenu.js';
import { EmptyState, ErrorState } from '../components/EmptyState.js';
import { Icon } from '../components/Icon.js';
import { Logo } from '../components/Logo.js';
import { Modal } from '../components/Modal.js';
import { StatTile } from '../components/StatTile.js';
import { labels } from '../labels.js';
import styles from './TablesPage.module.css';

type Confirming =
  | { kind: 'rotate'; table: TableWithSessionDTO }
  | { kind: 'close'; table: TableWithSessionDTO }
  | { kind: 'delete'; table: TableWithSessionDTO };

export function TablesPage() {
  const { auth } = useAuth();
  const canWrite = auth?.user.role === 'MANAGER' || auth?.user.role === 'OWNER';
  const currency = auth?.restaurant?.currency ?? 'XOF';

  const [tables, setTables] = useState<TableWithSessionDTO[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [banner, setBanner] = useState<string | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  const [editTable, setEditTable] = useState<TableWithSessionDTO | null>(null);
  const [qrPreview, setQrPreview] = useState<{ table: TableWithSessionDTO; url: string } | null>(null);
  const [confirming, setConfirming] = useState<Confirming | null>(null);
  const [tokenCopied, setTokenCopied] = useState(false);

  const load = useCallback(() => {
    return apiFetch<{ tables: TableWithSessionDTO[] }>('/admin/tables')
      .then((d) => { setTables(d.tables); setLoadError(false); })
      .catch(() => setLoadError(true))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const { socket } = useRealtimeSync(auth ? getAccessToken() : null, load);

  useEffect(() => {
    if (!socket) return;
    // session:opened manquait : une table ne passait jamais a « occupee »,
    // seulement a « libre ». C'est ce qui rendait le badge inutilisable.
    const refresh = () => load();
    const events = [
      'session:opened',
      'session:closed',
      'table:updated',
      'order:created',
      'order:status_changed',
    ] as const;

    for (const name of events) socket.on(name as never, refresh as never);
    return () => {
      for (const name of events) socket.off(name as never, refresh as never);
    };
  }, [socket, load]);

  const notify = (message: string) => {
    setBanner(message);
    window.setTimeout(() => setBanner(null), 5000);
  };

  const saveBlobDownload = async (path: string, filename: string) => {
    const blob = await apiFetchBlob(path);
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 100);
  };

  const downloadQr = async (t: TableWithSessionDTO, format: 'png' | 'svg') => {
    try {
      await saveBlobDownload(`/admin/tables/${t.id}/qrcode?format=${format}&size=512`, `qr-table-${t.label}.${format}`);
    } catch {
      notify(labels.tables.qrUnavailable);
    }
  };

  const openQrPreview = async (t: TableWithSessionDTO) => {
    try {
      const blob = await apiFetchBlob(`/admin/tables/${t.id}/qrcode?format=png&size=512`);
      setTokenCopied(false);
      setQrPreview({ table: t, url: URL.createObjectURL(blob) });
    } catch {
      notify(labels.tables.qrUnavailable);
    }
  };

  const closeQrPreview = () => {
    if (qrPreview) URL.revokeObjectURL(qrPreview.url);
    setQrPreview(null);
  };

  const runConfirmed = async () => {
    if (!confirming) return;
    const { kind, table } = confirming;
    setConfirming(null);
    try {
      if (kind === 'rotate') await apiFetch(`/admin/tables/${table.id}/rotate-qr`, { method: 'POST' });
      if (kind === 'close') await apiFetch(`/admin/tables/${table.id}/close-session`, { method: 'POST' });
      if (kind === 'delete') await apiFetch(`/admin/tables/${table.id}`, { method: 'DELETE' });
      await load();
    } catch {
      notify(kind === 'delete' ? labels.tables.deleteTableBlocked : labels.errors.generic);
    }
  };

  const downloadSheet = async () => {
    try {
      await saveBlobDownload('/admin/tables/qrcodes.pdf', 'planche-qr.pdf');
    } catch {
      notify(labels.tables.pdfUnavailable);
    }
  };

  const active = tables.filter((t) => t.isActive);
  const diners = tables.reduce((sum, t) => sum + t.occupancy.dinerCount, 0);
  const activeOrders = tables.reduce((sum, t) => sum + t.occupancy.activeOrderCount, 0);
  const pendingTotal = tables.reduce((sum, t) => sum + t.occupancy.pendingTotalMinor, 0);
  const occupied = tables.filter((t) => t.occupancy.dinerCount > 0).length;

  // Grouped by zone, docs/15 section 4.5. Tables without a zone come last.
  const zones = new Map<string, TableWithSessionDTO[]>();
  for (const t of tables) {
    const key = t.zone?.trim() || '';
    zones.set(key, [...(zones.get(key) ?? []), t]);
  }
  const zoneEntries = [...zones.entries()].sort(([a], [b]) => (a === '' ? 1 : b === '' ? -1 : a.localeCompare(b, 'fr')));

  return (
    <div className="page">
      <header className="page-header">
        <div>
          <h1 className="page-title">{labels.tables.title}</h1>
          <p className="page-subtitle">{labels.tables.subtitle}</p>
        </div>
        <div className="page-actions">
          <button onClick={downloadSheet} className="btn-ghost">
            <Icon name="print" size={18} />
            {labels.tables.downloadSheet}
          </button>
          {canWrite && (
            <button onClick={() => setAddOpen(true)} className="btn-primary">
              <Icon name="plus" size={18} />
              {labels.tables.addTable}
            </button>
          )}
        </div>
      </header>

      {banner && (
        <p role="alert" className="notice notice--warning">
          <Icon name="alert" size={18} />
          {banner}
        </p>
      )}

      {loading ? (
        <>
          <div className="kpi-grid">
            {Array.from({ length: 4 }).map((_, i) => <div key={i} className="skeleton" style={{ height: '5.5rem', borderRadius: 'var(--radius-lg)' }} />)}
          </div>
          <div className={styles.grid} style={{ marginTop: 'var(--space-8)' }}>
            {Array.from({ length: 6 }).map((_, i) => <div key={i} className="skeleton" style={{ height: '11rem', borderRadius: 'var(--radius-lg)' }} />)}
          </div>
        </>
      ) : loadError ? (
        <ErrorState onRetry={load} />
      ) : tables.length === 0 ? (
        <div className="card">
          <EmptyState icon="qr" text={labels.tables.empty}>
            {canWrite && (
              <button className="btn-primary" onClick={() => setAddOpen(true)}>
                <Icon name="plus" size={18} />
                {labels.tables.addTable}
              </button>
            )}
          </EmptyState>
        </div>
      ) : (
        <>
          <div className="kpi-grid">
            <StatTile icon="table" label={labels.tables.kpiTables} value={active.length} meta={labels.tables.kpiOccupied(occupied)} />
            <StatTile icon="users" label={labels.tables.kpiDiners} value={diners} />
            <StatTile icon="receipt" label={labels.tables.kpiOrders} value={activeOrders} />
            <StatTile icon="cash" label={labels.tables.kpiPending} value={formatMoney(pendingTotal, currency)} />
          </div>

          {zoneEntries.map(([zone, list]) => (
            <section key={zone || 'none'} className="section">
              <header className="section-header">
                <h2 className="section-title">{zone || labels.tables.noZone}</h2>
                <span className="count">{list.length}</span>
              </header>
              <div className={styles.grid}>
                {list.map((t) => (
                  <TableCard
                    key={t.id}
                    table={t}
                    canWrite={canWrite}
                    currency={currency}
                    onQrPreview={() => void openQrPreview(t)}
                    onEdit={() => setEditTable(t)}
                    onRotateQr={() => setConfirming({ kind: 'rotate', table: t })}
                    onCloseSession={() => setConfirming({ kind: 'close', table: t })}
                    onDelete={() => setConfirming({ kind: 'delete', table: t })}
                    onDownloadPng={() => void downloadQr(t, 'png')}
                    onDownloadSvg={() => void downloadQr(t, 'svg')}
                  />
                ))}
              </div>
            </section>
          ))}
        </>
      )}

      {addOpen && (
        <TableDialog onClose={() => setAddOpen(false)} onSaved={async () => { setAddOpen(false); await load(); }} onError={notify} />
      )}
      {editTable && (
        <TableDialog table={editTable} onClose={() => setEditTable(null)} onSaved={async () => { setEditTable(null); await load(); }} onError={notify} />
      )}

      {qrPreview && (
        <Modal
          title={labels.tables.previewQr}
          description={labels.app.tableLabel(qrPreview.table.label)}
          onClose={closeQrPreview}
          footer={
            <>
              <button type="button" className="btn-ghost" onClick={() => void downloadQr(qrPreview.table, 'svg')}>
                <Icon name="download" size={18} />
                SVG
              </button>
              <button type="button" className="btn-primary" onClick={() => void downloadQr(qrPreview.table, 'png')}>
                <Icon name="download" size={18} />
                {labels.tables.downloadPng}
              </button>
            </>
          }
        >
          {/* Table tent, Design System v1.0 section 07: Creme ground, QR in a
              white frame, the message in Bold, the logo under the code. */}
          <div className={styles.tent}>
            <p className={styles.tentMessage}>{labels.tables.tentMessage}</p>
            <div className={styles.tentCode}>
              <img src={qrPreview.url} alt={`QR code ${labels.app.tableLabel(qrPreview.table.label)}`} width={220} height={220} />
            </div>
            <p className={styles.tentTable}>{labels.app.tableLabel(qrPreview.table.label)}</p>
            <Logo size={24} withWordmark />
          </div>

          <div className="field" style={{ marginTop: 'var(--space-5)' }}>
            <span className="field-label">{labels.tables.tokenLabel}</span>
            <div className={styles.token}>
              <code className="mono">{qrPreview.table.qrToken}</code>
              <button
                type="button"
                onClick={() => {
                  navigator.clipboard?.writeText(qrPreview.table.qrToken).then(
                    () => { setTokenCopied(true); window.setTimeout(() => setTokenCopied(false), 2500); },
                    () => undefined,
                  );
                }}
                className="btn-ghost btn-sm"
              >
                <Icon name={tokenCopied ? 'check' : 'copy'} size={16} />
                {tokenCopied ? labels.tables.copiedShort : labels.tables.copyToken}
              </button>
            </div>
            <p className="field-hint" role={tokenCopied ? 'status' : undefined}>
              {tokenCopied ? labels.tables.copied : labels.tables.tokenHint}
            </p>
          </div>
        </Modal>
      )}

      {confirming && (
        <ConfirmDialog
          title={
            confirming.kind === 'rotate' ? labels.tables.rotateTitle(confirming.table.label)
              : confirming.kind === 'close' ? labels.tables.closeSessionTitle(confirming.table.label)
                : labels.tables.deleteTableConfirm(confirming.table.label)
          }
          message={
            confirming.kind === 'rotate' ? labels.tables.rotateConfirm
              : confirming.kind === 'close' ? labels.tables.closeSessionHelp
                : labels.tables.deleteTableHelp
          }
          confirmLabel={
            confirming.kind === 'rotate' ? labels.tables.rotate
              : confirming.kind === 'close' ? labels.tables.closeSession
                : labels.tables.deleteTable
          }
          destructive={confirming.kind !== 'close'}
          onConfirm={() => void runConfirmed()}
          onCancel={() => setConfirming(null)}
        />
      )}
    </div>
  );
}

/* -- TableCard: one main action and a "more" menu -- */

function TableCard({ table, canWrite, currency, onQrPreview, onEdit, onRotateQr, onCloseSession, onDelete, onDownloadPng, onDownloadSvg }: {
  table: TableWithSessionDTO;
  canWrite: boolean;
  currency: string;
  onQrPreview: () => void;
  onEdit: () => void;
  onRotateQr: () => void;
  onCloseSession: () => void;
  onDelete: () => void;
  onDownloadPng: () => void;
  onDownloadSvg: () => void;
}) {
  const moreRef = useRef<HTMLButtonElement>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const { dinerCount, activeOrderCount, pendingTotalMinor } = table.occupancy;
  const busy = dinerCount > 0;

  return (
    <article className={`${styles.card} ${busy ? styles.cardBusy : ''} ${table.isActive ? '' : styles.cardInactive}`}>
      <header className={styles.cardHead}>
        <div className="grow">
          <h3 className={styles.label}>{labels.app.tableLabel(table.label)}</h3>
          <p className={styles.caption}>
            {table.capacity != null ? labels.tables.capacity(table.capacity) : labels.tables.noCapacity}
          </p>
        </div>
        {!table.isActive ? (
          <span className="badge badge--outline">{labels.tables.inactive}</span>
        ) : (
          // Des convives connectes, non une presence physique : le systeme ne
          // sait que cela. Le compte agrege toutes les sessions ouvertes de la
          // table, une par convive depuis l'ADR 0010.
          <span className={`badge badge--dot ${busy ? 'badge--ready' : 'badge--neutral'}`}>
            {busy ? labels.tables.diners(dinerCount) : labels.tables.free}
          </span>
        )}
      </header>

      <div className={styles.cardBody}>
        {activeOrderCount > 0 ? (
          <>
            <span className={styles.amount}>{formatMoney(pendingTotalMinor, currency)}</span>
            <span className={styles.caption}>{labels.tables.pendingOrders(activeOrderCount)}</span>
          </>
        ) : (
          <span className={styles.caption}>{labels.tables.noActiveOrder}</span>
        )}
      </div>

      <footer className={styles.cardFoot}>
        <button onClick={onQrPreview} className="btn-secondary btn-sm grow">
          <Icon name="qr" size={16} />
          {labels.tables.previewQr}
        </button>
        <button
          ref={moreRef}
          className="btn-icon btn-icon--quiet btn-icon--sm"
          onClick={() => setMenuOpen(!menuOpen)}
          aria-expanded={menuOpen}
          aria-haspopup="true"
          aria-label={labels.tables.moreActionsFor(table.label)}
        >
          <Icon name="more" size={18} />
        </button>
        <DropdownMenu open={menuOpen} onClose={() => setMenuOpen(false)} anchorRef={moreRef}>
          <DropdownItem onClick={() => { setMenuOpen(false); onDownloadPng(); }} icon={<Icon name="download" size={18} />}>
            {labels.tables.downloadPng}
          </DropdownItem>
          <DropdownItem onClick={() => { setMenuOpen(false); onDownloadSvg(); }} icon={<Icon name="download" size={18} />}>
            {labels.tables.downloadSvg}
          </DropdownItem>
          {busy && canWrite && (
            <DropdownItem onClick={() => { setMenuOpen(false); onCloseSession(); }} icon={<Icon name="logout" size={18} />}>
              {labels.tables.closeSession}
            </DropdownItem>
          )}
          {canWrite && (
            <>
              <div className="dropdown-separator" />
              <DropdownItem onClick={() => { setMenuOpen(false); onEdit(); }} icon={<Icon name="edit" size={18} />}>
                {labels.tables.editTable}
              </DropdownItem>
              <DropdownItem onClick={() => { setMenuOpen(false); onRotateQr(); }} icon={<Icon name="rotate" size={18} />}>
                {labels.tables.rotate}
              </DropdownItem>
              <DropdownItem onClick={() => { setMenuOpen(false); onDelete(); }} icon={<Icon name="trash" size={18} />} danger>
                {labels.tables.deleteTable}
              </DropdownItem>
            </>
          )}
        </DropdownMenu>
      </footer>
    </article>
  );
}

/* -- Table dialog -- */

function TableDialog({ table, onClose, onSaved, onError }: {
  table?: TableWithSessionDTO;
  onClose: () => void;
  onSaved: () => Promise<void>;
  onError: (message: string) => void;
}) {
  const [labelValue, setLabelValue] = useState(table?.label ?? '');
  const [capacity, setCapacity] = useState(table?.capacity?.toString() ?? '');
  const [zone, setZone] = useState(table?.zone ?? '');
  const [saving, setSaving] = useState(false);
  const empty = labelValue.trim().length === 0;

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    if (empty || saving) return;
    const body: Record<string, unknown> = { label: labelValue.trim() };
    if (capacity) body.capacity = parseInt(capacity, 10);
    body.zone = zone.trim() || null;
    setSaving(true);
    try {
      await apiFetch(table ? `/admin/tables/${table.id}` : '/admin/tables', {
        method: table ? 'PATCH' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      await onSaved();
    } catch (e) {
      onError(e instanceof Error && e.message ? e.message : labels.errors.generic);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title={table ? labels.tables.editTable : labels.tables.addTable}
      description={table ? undefined : labels.tables.addHelp}
      onClose={onClose}
      onSubmit={(e) => void save(e)}
      locked={saving}
      footer={
        <>
          <button type="button" onClick={onClose} className="btn-ghost" disabled={saving}>{labels.tables.cancel}</button>
          <button type="submit" disabled={empty || saving} className="btn-primary">{labels.tables.save}</button>
        </>
      }
    >
      <div className="form-grid">
        <div className="field">
          <label htmlFor="table-label" className="field-label">{labels.tables.tableLabelField}</label>
          <input id="table-label" className="input" value={labelValue} onChange={(e) => setLabelValue(e.target.value)} placeholder="12" required autoFocus />
        </div>
        <div className="form-grid form-grid--2">
          <div className="field">
            <label htmlFor="table-capacity" className="field-label">{labels.tables.tableCapacity}</label>
            <input id="table-capacity" className="input" type="number" min="1" value={capacity} onChange={(e) => setCapacity(e.target.value)} placeholder="4" />
          </div>
          <div className="field">
            <label htmlFor="table-zone" className="field-label">{labels.tables.tableZone}</label>
            <input id="table-zone" className="input" value={zone} onChange={(e) => setZone(e.target.value)} placeholder={labels.tables.zonePlaceholder} />
          </div>
        </div>
      </div>
    </Modal>
  );
}
